import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { publicApi } from "../lib/api";

const SettingsContext = createContext({
  enrollmentOpen: false,
  reenrollmentOpen: false,
  feeTiers: { one_child: 180, two_children: 320, three_plus_children: 420 },
  activeYear: null,
  refresh: () => {},
  loading: true,
});

export function SettingsProvider({ children }) {
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const [reenrollmentOpen, setReenrollmentOpen] = useState(false);
  const [feeTiers, setFeeTiers] = useState({ one_child: 180, two_children: 320, three_plus_children: 420 });
  const [activeYear, setActiveYear] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await publicApi.get("/settings");
      setEnrollmentOpen(!!data.enrollment_open);
      setReenrollmentOpen(!!data.reenrollment_open);
      if (data.fee_tiers) setFeeTiers(data.fee_tiers);
      setActiveYear(data.active_year || null);
    } catch {
      setEnrollmentOpen(false);
      setReenrollmentOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Rafraîchir périodiquement (toutes les 2 minutes) et au retour de la page
  // pour que les changements d'ouverture d'inscription soient immédiatement visibles.
  useEffect(() => {
    const interval = setInterval(refresh, 2 * 60 * 1000);
    const onFocus = () => refresh();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({ enrollmentOpen, reenrollmentOpen, feeTiers, activeYear, loading, refresh, setEnrollmentOpen, setReenrollmentOpen }),
    [enrollmentOpen, reenrollmentOpen, feeTiers, activeYear, loading, refresh]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export const useSettings = () => useContext(SettingsContext);
